import { AuthSigningRequestArgs } from './api/authSigning.ts';
import { AuthTokenRequestArgs } from './api/authToken.ts';
import { LibraryArtistRequestArgsInput } from './api/meLibraryArtistWithAlbums.ts';
import { MeLibraryArtistsRequestArgsInput } from './api/meLibraryArtists.ts';
import {
  MeLibraryPlaylistsData,
  MeLibraryPlaylistsRequestArgs,
} from './api/meLibraryPlaylists.ts';
import {
  MeLibraryRecentlyAddedData,
  MeLibraryRecentlyAddedRequestArgs,
} from './api/meLibraryRecentlyAdded.ts';
import { PlaylistMediaPageRequestArgs } from './api/playlistMedia.ts';
import { RecommendationRequestArgs } from './api/meRecommendationsId.ts';
import {
  AlbumItem,
  PlaylistItem,
  StationItem,
  TrackItem,
} from './apiSchema.ts';
import { MeLibraryAlbumsRequestArgs } from './api/meLibraryAlbums.ts';
import { AlbumMediaPageRequestArgs } from './api/albumMedia.ts';
import { MeRecommendationsRequestArgs } from './api/meRecommendations.ts';
import { MeRecentlyPlayedRequestArgs } from './api/meRecentlyPlayed.ts';
import { MeLibrarySongsRequestArgs } from './api/meLibrarySongs.ts';
import { SearchRequestArgs } from './api/catalogSearch.ts';
import { ArtistMediaPageRequestArgs } from './api/catalogArtistsId.ts';
import { CatalogArtistsIdViewRequestArgs } from './api/catalogArtistsIdView.ts';
import { ResourceRelationshipRequestArgs } from './api/resourceRelationship.ts';
import { RestoreRequestArgs } from './api/restore.ts';

export type Literal<T extends { [key: string]: string }> = T[keyof T];

export type MKErr = {
  errorCode: string;
  message: string;
};

export type Ok<T extends object> = { ok: true; value?: T };
export type Err = { ok: false; error: MKErr };
export type Result<T extends object> = Ok<T> | Err;

// Request Arguments
// ---------------------

export type Queue = {
  type: 'album' | 'playlist' | 'track' | 'station';
  id: string;
};

export type QueueIndex = {
  index: number;
};

export type PlayTrackAtRequestArgs = {
  position: number;
};

export type PlayToRequestArgs = {
  queue: Queue;
};

export type PlayMediaItemRequestArgs = {
  trackListQueue: Queue;
  trackItemQueue: Queue | QueueIndex;
};

export type ChangeRepeatModeRequestArgs = {
  repeatMode: number; // none: 0, one: 1, all: 2
};

export type ChangeVolumeRequestArgs = {
  volume: number;
};

export type SeekToTimeRequestArgs = {
  time: number; // in seconds
};

// Native -> HTML
// ---------------

export const NotificationPayloadType = {
  NotifyDeveloperToken: 'notifyDeveloperToken',
  Authorize: 'authorize',
  Unauthorize: 'unauthorize',
} as const;
export type NotificationPayloadType = Literal<typeof NotificationPayloadType>;

export type NotificationValueMap = {
  [NotificationPayloadType.NotifyDeveloperToken]: {
    token: string | undefined;
  };
};

export type NotificationPayload = {
  [K in NotificationPayloadType]: K extends keyof NotificationValueMap
    ? { type: K; value: NotificationValueMap[K] }
    : { type: K };
}[NotificationPayloadType];

// Native -> HTML -> Native
// -------------------------

export const CommandPayloadType = {
  AuthSigning: 'requestAuthSigning',
  AuthToken: 'requestAuthToken',
  Play: 'play',
  Pause: 'pause',
  Stop: 'stop',
  SkipToNextItem: 'skipToNextItem',
  SkipToPreviousItem: 'skipToPreviousItem',
  SeekToTime: 'seekToTime',
  PlayTo: 'playTo',
  PlayToMediaItem: 'playToMediaItem',
  PlayTrackAt: 'playTrackAt',
  PlayNext: 'playNext',
  PlayLater: 'playLater',
  ClearQueue: 'clearQueue',
  CurrentPlayerStatus: 'currentPlayerStatus',
  ChangeVolume: 'changeVolume',
  ChangeRepeatMode: 'changeRepeatMode',
  Mute: 'mute',
  Unmute: 'unmute',
  ToggleShuffleMode: 'toggleShuffleMode',
  Restore: 'restore',

  // Music API
  GetRecommendations: 'getRecommendations',
  GetRecommendGroups: 'getRecommendGroups',
  GetRecommendation: 'getRecommendation',
  GetRecentlyPlayed: 'getRecentlyPlayed',
  GetMeLibraryPlaylists: 'getMeLibraryPlaylists',
  GetMeLibraryRecentlyAdded: 'getMeLibraryRecentlyAdded',
  GetPlaylistMedia: 'getPlaylistMedia',
  GetMeLibraryAlbums: 'getMeLibraryAlbums',
  GetAlbumMedia: 'getAlbumMedia',
  GetMeLibraryArtists: 'getMeLibraryArtists',
  GetMeLibraryArtist: 'getMeLibraryArtist',
  GetMeLibrarySongs: 'getMeLibrarySongs',
  GetArtistMedia: 'getArtistMedia',
  GetCatalogArtistsIdView: 'getCatalogArtistsIdView',
  GetResourceRelationship: 'getResourceRelationship',
  Search: 'search',
} as const;
export type CommandPayloadType = Literal<typeof CommandPayloadType>;

export type CommandValueMap = {
  [CommandPayloadType.AuthSigning]: AuthSigningRequestArgs;
  [CommandPayloadType.AuthToken]: AuthTokenRequestArgs;
  [CommandPayloadType.PlayTo]: PlayToRequestArgs;
  [CommandPayloadType.PlayToMediaItem]: PlayMediaItemRequestArgs;
  [CommandPayloadType.PlayTrackAt]: PlayTrackAtRequestArgs;
  [CommandPayloadType.PlayNext]: Queue;
  [CommandPayloadType.PlayLater]: Queue;
  [CommandPayloadType.ChangeRepeatMode]: ChangeRepeatModeRequestArgs;
  [CommandPayloadType.ChangeVolume]: ChangeVolumeRequestArgs;
  [CommandPayloadType.Restore]: RestoreRequestArgs;
  [CommandPayloadType.GetRecommendGroups]: MeRecommendationsRequestArgs;
  [CommandPayloadType.GetRecommendation]: RecommendationRequestArgs;
  [CommandPayloadType.GetRecentlyPlayed]: MeRecentlyPlayedRequestArgs;
  [CommandPayloadType.GetMeLibraryPlaylists]: MeLibraryPlaylistsRequestArgs;
  [CommandPayloadType.GetMeLibraryRecentlyAdded]:
    MeLibraryRecentlyAddedRequestArgs;
  [CommandPayloadType.GetPlaylistMedia]: PlaylistMediaPageRequestArgs;
  [CommandPayloadType.GetMeLibraryAlbums]: MeLibraryAlbumsRequestArgs;
  [CommandPayloadType.GetAlbumMedia]: AlbumMediaPageRequestArgs;
  [CommandPayloadType.GetMeLibraryArtists]: MeLibraryArtistsRequestArgsInput;
  [CommandPayloadType.GetMeLibraryArtist]: LibraryArtistRequestArgsInput;
  [CommandPayloadType.GetMeLibrarySongs]: MeLibrarySongsRequestArgs;
  [CommandPayloadType.GetArtistMedia]: ArtistMediaPageRequestArgs;
  [CommandPayloadType.GetCatalogArtistsIdView]: CatalogArtistsIdViewRequestArgs;
  [CommandPayloadType.GetResourceRelationship]: ResourceRelationshipRequestArgs;
  [CommandPayloadType.Search]: SearchRequestArgs;
  [CommandPayloadType.SeekToTime]: SeekToTimeRequestArgs;
};

export type CommandPayload = {
  [K in CommandPayloadType]: K extends keyof CommandValueMap
    ? { type: K; value: CommandValueMap[K] }
    : { type: K };
}[CommandPayloadType];

//export type TrackItem = Queue; // TODO

export type CurrentPlayerStatus = {
  // The duration of the nowPlayingItem, in seconds.
  currentPlaybackDuration: number;

  // Progress percentage between 0 and 1 indicating the current play head position for the nowPlayingItem.
  // Useful for showing a playback progress bar UI, for instance.
  currentPlaybackProgress: number;

  // The current position of the play head for the nowPlayingItem, in seconds.
  currentPlaybackTime: number;

  // The current remaining playback time for the nowPlayingItem, in seconds.
  currentPlaybackTimeRemaining: number;

  // This is set to true after the user successfully signs in and authorizes the application via the authorize() method,
  // or upon configuring the MusicKit instance if the user had previously authorized your application.
  isAuthorized: boolean;

  // Indicates whether the player is in the playing state.
  isPlaying: boolean;

  // This is the MediaItem that is currently playing from the Queue.
  nowPlayingItem: TrackItem | null;

  //The index of the nowPlayingItem in the current playback Queue.
  nowPlayingItemIndex: number;

  // The current playback state of the player.
  playbackRate: number; // TODO: Change to enum PlaybackState

  // Indicates whether the current playback queue is empty.
  queueIsEmpty: boolean;

  // The current playback queue of the music player.
  queue: TrackItem[];

  // Set this to an Enum value from MusicKit.PlayerRepeatMode to control the repeat behavior during playback.
  // The default value is MusicKit.PlayerRepeatMode.none.
  repeatMode: number; // TODO: Change to enum RepeatMode

  // Indicates whether the player is in shuffle mode.
  shuffleMode: boolean;

  // This is the id of the configured storefront for the instance of MusicKit.
  storefrontId: string;

  // The current volume level of the player, between 0.0 and 1.0.
  volume: number;

  // Container for the current media item.
  container: AlbumItem | PlaylistItem | StationItem | null;
};

export type PlaylistList = MeLibraryPlaylistsData;
export type LibraryTopResponse = MeLibraryRecentlyAddedData;

// HTML -> Native events
// ----------------------

export type portConnectedEvent = {
  type: 'portConnected';
};

export type playerReadyEvent = {
  type: 'playerReady';
  value: {
    isAuthorized: boolean;
  };
};

export type authorizationStatusDidChangeEvent = {
  type: 'authorizationStatusDidChange';
  value: {
    isAuthorized: boolean;
    authorizationStatus: number; // MusicKit.AuthStatus;
  };
};

export type drmUnsupportedEvent = {
  type: 'drmUnsupported';
};

export type mediaCanPlayEvent = {
  type: 'mediaCanPlay';
};

export type mediaItemStateDidChangeEvent = {
  type: 'mediaItemStateDidChange';
  value: {
    id: string;
    state: number;
  };
};

export type mediaPlaybackErrorEvent = {
  type: 'mediaPlaybackError';
  value: {
    message: string;
    errorCode: string;
  };
};

export type nowPlayingItemDidChangeEvent = {
  type: 'nowPlayingItemDidChange';
  value: {
    item: TrackItem | undefined;
    container: AlbumItem | PlaylistItem | StationItem | null | undefined;
  };
};

export type playbackStateDidChangeEvent = {
  type: 'playbackStateDidChange';
  value: {
    oldState: MusicKit.PlaybackStates;
    state: MusicKit.PlaybackStates;
  };
};

export type playbackProgressDidChangeEvent = {
  type: 'playbackProgressDidChange';
  value: {
    progress: number;
  };
};

export type playbackVolumeDidChangeEvent = {
  type: 'playbackVolumeDidChange';
  value: {
    volume: number;
  };
};

export type queueIsReadyEvent = {
  type: 'queueIsReady';
};

export type queueItemsDidChangeEvent = {
  type: 'queueItemsDidChange';
  value: {
    items: TrackItem[];
  };
};

export type queuePositionDidChangeEvent = {
  type: 'queuePositionDidChange';
  value: {
    oldPosition: number;
    position: number;
  };
};

export type storefrontCountryCodeDidChangeEvent = {
  type: 'storefrontCountryCodeDidChange';
  value: {
    storefrontCountryCode: string;
  };
};

export type contentRestrictionEvent = {
  type: 'contentRestriction';
  value: {
    item: TrackItem;
  };
};

export type MKEvent =
  | portConnectedEvent
  | playerReadyEvent
  | authorizationStatusDidChangeEvent
  | drmUnsupportedEvent
  | mediaCanPlayEvent
  | mediaItemStateDidChangeEvent
  | mediaPlaybackErrorEvent
  | nowPlayingItemDidChangeEvent
  | playbackStateDidChangeEvent
  | playbackProgressDidChangeEvent
  | playbackVolumeDidChangeEvent
  | queueIsReadyEvent
  | queueItemsDidChangeEvent
  | queuePositionDidChangeEvent
  | storefrontCountryCodeDidChangeEvent
  | contentRestrictionEvent;
